/**
 * useLiveStatus Hook
 *
 * Provides real-time status data for the TopBar:
 * - Online device count (polled from Central Server API)
 * - Running execution count (real-time from Firestore test_runs collection)
 */

import { useState, useEffect, useCallback } from 'react';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { subscribeToTestRuns } from '@/services/testConfigurationService';
import { useAuth } from '@/contexts/AuthContext';
import { useVisibilityAwareInterval } from '@/hooks/useVisibilityAwareInterval';
import { filterSyntheticDemoDevices } from '@/hooks/useDeviceVisibility';
import type { TestRun } from '@simcheck/shared-types';

export interface LiveStatusData {
  // Device stats
  onlineDevices: number;
  totalDevices: number;
  onlineModems: number;
  totalModems: number;

  // Execution stats (from test_runs collection)
  runningExecutions: number;
  pendingExecutions: number;

  // Loading/error states
  devicesLoading: boolean;
  devicesError: string | null;
}

interface UseLiveStatusOptions {
  /** Polling interval for device status in milliseconds (default: 60000) */
  pollingInterval?: number;
  /** Whether to enable polling (default: true) */
  enablePolling?: boolean;
}

/**
 * Hook for live status data displayed in the TopBar
 */
export function useLiveStatus(options: UseLiveStatusOptions = {}): LiveStatusData {
  const { pollingInterval = 60000, enablePolling = true } = options;
  const { activeOrganization, activeOrganizationId } = useAuth();

  // Test runs state (real-time from Firestore test_runs collection)
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);

  // Device stats state
  const [deviceStats, setDeviceStats] = useState({
    onlineDevices: 0,
    totalDevices: 0,
    onlineModems: 0,
    totalModems: 0,
    loading: true,
    error: null as string | null,
  });

  // Fetch device status
  const fetchDeviceStatus = useCallback(async () => {
    try {
      const allDevices = await edgeDeviceService.getDevices();
      // Synthetic demo devices only count while you're inside their owning org —
      // otherwise a seeded demo fleet inflates the global tally for super-admins.
      const devices = filterSyntheticDemoDevices(allDevices, activeOrganizationId || null);

      // Count online devices
      const onlineDevices = devices.filter(d => d.status === 'online').length;

      // Count modems
      let totalModems = 0;
      let onlineModems = 0;

      devices.forEach(device => {
        const modems = device.modems || [];
        totalModems += modems.length;
        onlineModems += modems.filter(m => m.status === 'connected').length;
      });

      setDeviceStats({
        onlineDevices,
        totalDevices: devices.length,
        onlineModems,
        totalModems,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error fetching device status:', error);
      setDeviceStats(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch device status',
      }));
    }
    // Re-fetch on org switch so the synthetic-demo scope is re-applied.
  }, [activeOrganizationId]);

  // Initial fetch
  useEffect(() => {
    fetchDeviceStatus();
  }, [fetchDeviceStatus]);

  // Visibility-aware polling: pauses when tab is hidden, resumes on focus
  useVisibilityAwareInterval(fetchDeviceStatus, pollingInterval, enablePolling);

  // Subscribe to test_runs collection for real-time execution counts
  // Uses same filter as Tests page: ['running', 'initializing']
  useEffect(() => {
    if (!activeOrganization?.id) {
      setTestRuns([]);
      return;
    }

    const unsubscribe = subscribeToTestRuns(
      activeOrganization.id,
      (runs) => {
        setTestRuns(runs);
      },
      {
        status: ['running', 'initializing'],
        limitCount: 100,
      }
    );

    return () => unsubscribe();
  }, [activeOrganization?.id]);

  // Count running executions (running + initializing)
  const runningExecutions = testRuns.length;
  // Pending is not tracked separately in test_runs - set to 0 for now
  const pendingExecutions = 0;

  return {
    onlineDevices: deviceStats.onlineDevices,
    totalDevices: deviceStats.totalDevices,
    onlineModems: deviceStats.onlineModems,
    totalModems: deviceStats.totalModems,
    runningExecutions,
    pendingExecutions,
    devicesLoading: deviceStats.loading,
    devicesError: deviceStats.error,
  };
}
